import {
  eachDayOfInterval,
  eachWeekOfInterval,
  endOfWeek,
  format,
  isAfter,
  isBefore,
  isSameDay,
  isSameMonth,
  parse,
  startOfMonth,
  startOfWeek,
} from "date-fns";
import React from "react";
import { useParams } from "react-router-dom";
import Event from "./Event";
import Weather from "./Weather";
import getStartAndEndDate from "./utils/getStartAndEndDate";
import useSheets from "./utils/useSheets";

export default function WeekView(props) {
  const match = useParams<{ wing?: string; community: string }>();
  const {
    startDate,
    endDate,
    events: mainEvents,
    today,
  } = useSheets({
    view: "week",
    date: props.date,
    location: props.location || match.wing,
  });
  let extraEvents = [];

  if (!isSameMonth(startDate, endDate)) {
    const extraEventsData = useSheets({
      view: "week",
      date: isSameMonth(today, endDate) ? startOfMonth(startDate) : startOfMonth(endDate),
      location: props.location || match.wing,
    });
    extraEvents = extraEventsData.events;
  }

  const events = [...mainEvents, ...extraEvents];

  const interval = eachWeekOfInterval({
    start: startDate,
    end: endDate,
  });

  let thisWeek = `${format(startDate, "MMMM d")}–${format(endDate, "d")}`;

  if (!isSameMonth(endDate, startDate)) {
    thisWeek = `${format(startDate, "MMMM d")}–${format(endDate, "MMMM d")}`;
  }

  const moveIns = events
    .filter((e) => e["Event-Name"] === "Move In")
    .map((e) => e["Event-Location"]);

  return (
    <React.Fragment>
      <header className="header header--week">
        <div className="header__logo">
          <img src={`/img/logo-stacked.png`} alt="logo" />
        </div>
        <h1 className="header__title">
          {format(today, "EEEE, MMMM d")}{" "}
          <span className="header__title-date">{format(today, "h:mm aaaa")}</span>
        </h1>
        <div className="header__weather">
          <Weather />
        </div>
      </header>
      <header className="header">
        <h1 className="header__title">This Week: {thisWeek}</h1>
      </header>
      <div className="calendar-container">
        <main className={`calendar calendar--week ${match.community}`}>
          {interval.map((w, i) => {
            return (
              <div className="week" key={i}>
                {eachDayOfInterval({
                  start: startOfWeek(w),
                  end: endOfWeek(w),
                }).map((d, i) => {
                  const eventsForDay = events.filter((e) => {
                    const eventDate = parse(e["Event-Date"], "M/d/yy", new Date());

                    // console.log("eventDate", eventDate);
                    return (
                      isSameDay(eventDate, d) &&
                      e["Event-Name"] !== "Move In" &&
                      !e["Memory Care Calendar?"]
                    );
                  });

                  // if (!isSameMonth(d, startDate)) {
                  //   return <div className="day day--empty" key={i}></div>;
                  // }

                  return (
                    <div className="day day--week" key={i}>
                      <div className="day__date day__date--week">
                        <span className="weekdays__day weekdays__day--week">
                          {format(d, "EEEE")}
                        </span>{" "}
                        {d.getDate()}
                      </div>
                      <div className="day__content">
                        {eventsForDay
                          .filter((e) => e["Holiday?"])
                          .map((ee, i) => (
                            <Event
                              key={i}
                              event={ee}
                              date={d}
                              type="week"
                              community={match.community}
                            />
                          ))}
                        {eventsForDay
                          .filter((e) => !e["Holiday?"])
                          .sort((a, b) => {
                            const aDates = getStartAndEndDate(a);
                            const bDates = getStartAndEndDate(b);
                            return aDates.startDate.getTime() - bDates.startDate.getTime();
                          })
                          .map((ee, i) => (
                            <Event
                              key={i}
                              event={ee}
                              date={d}
                              type="week"
                              community={match.community}
                            />
                          ))}
                        {eventsForDay.length === 0 && <div className="day day--empty" />}
                      </div>
                    </div>
                  );
                })}
              </div>
            );
          })}
        </main>
        <div className="featured">
          <div className="featured__title">Event Highlights</div>
          <div className="featured__content">
            {events
              .filter((e) => {
                const { startDate: eventStartDate } = getStartAndEndDate(e);
                return (
                  e["Event-Highlight?"] &&
                  isBefore(eventStartDate, endDate) &&
                  isAfter(eventStartDate, startDate)
                );
              })
              .sort((a, b) => {
                const { startDate: startDateA } = getStartAndEndDate(a);
                const { startDate: startDateB } = getStartAndEndDate(b);

                return startDateA.getTime() - startDateB.getTime();
              })
              .map((e, i) => {
                return <Event key={i} event={e} type="highlight" community={match.community} />;
              })}

            {moveIns.length > 0 && (
              <div>
                <div className="featured__title featured__title--sub">Moving in This Week!</div>
                <div className="featured__content--sub">
                  {moveIns.map((m) => {
                    return <div key={m}>{m}</div>;
                  })}
                </div>
              </div>
            )}
          </div>
        </div>
      </div>
      <div className="nav">
        <div className="nav__legend">
          <div className="legend__title">All times subject to change.</div>
          <div className="legend__content">
            <div className="legend__item">
              <div className="legend__icon legend__icon--center"></div>
              <div className="legend__item-title">Center</div>
            </div>
            <div className="legend__item">
              <div className="legend__icon legend__icon--a-wing"></div>
              <div className="legend__item-title">A Wing</div>
            </div>

            {match.community !== "wakefield" && match.community !== "apex" && (
              <div className="legend__item">
                <div className="legend__icon legend__icon--b-wing"></div>
                <div className="legend__item-title">B Wing</div>
              </div>
            )}

            <div className="legend__item">
              <div className="legend__icon legend__icon--c-wing"></div>
              <div className="legend__item-title">C Wing</div>
            </div>

            {match.community !== "fuquay" &&
              match.community !== "wakefield" &&
              match.community !== "apex" && (
                <div className="legend__item">
                  <div className="legend__icon legend__icon--d-wing"></div>
                  <div className="legend__item-title">D Wing</div>
                </div>
              )}

            {(match.community === "fuquay" ||
              match.community === "wakefield" ||
              match.community === "apex") && (
              <div className="legend__item">
                <div className="legend__icon legend__icon--outdoor"></div>
                <div className="legend__item-title">Outdoor</div>
              </div>
            )}
          </div>
        </div>
      </div>
    </React.Fragment>
  );
}
